/**
 * Shared API path routing for server and browser clients.
 * Commerce -> ERP donations, CMS -> Websites.
 * Legacy jood-api (:3036) is used only when USE_LEGACY_API=true.
 */
export type ApiRoutingTarget = "erp" | "legacy" | "websites";

export type ResolvedApiTarget = {
  target: ApiRoutingTarget;
  /** Path sent to the resolved backend (may be rewritten for websites). */
  path: string;
};

const LEGACY_PATH_PREFIXES = [
  "/content/news",
  "/content/faq",
  "/association-extensions",
  "/reports/",
  "/attachments/",
] as const;

function isLegacyApiEnabled(): boolean {
  return (
    typeof process !== "undefined" &&
    process.env.USE_LEGACY_API === "true"
  );
}

export function resolveApiTarget(path: string): ResolvedApiTarget {
  const normalized = path.startsWith("/") ? path : `/${path}`;

  if (normalized.startsWith("/content/portal-pages")) {
    return {
      target: "websites",
      path: normalized.replace("/content/portal-pages", "/public/portal-pages"),
    };
  }
  if (normalized.startsWith("/content/portal-menus")) {
    return {
      target: "websites",
      path: normalized.replace("/content/portal-menus", "/public/portal-menus"),
    };
  }
  const homepageMatch = normalized.match(/^\/homepage\/bundle\/(\d+)/);
  if (homepageMatch) {
    return {
      target: "websites",
      path: `/public/homepage/bundle?associationId=${homepageMatch[1]}`,
    };
  }

  if (
    isLegacyApiEnabled() &&
    LEGACY_PATH_PREFIXES.some((prefix) => normalized.startsWith(prefix))
  ) {
    return { target: "legacy", path: normalized };
  }

  return { target: "erp", path: normalized };
}

/** Browser/public path prefixes (relative to origin). */
export function resolveClientApiBasePath(target: ApiRoutingTarget): string {
  switch (target) {
    case "legacy":
      return "/api/legacy/v1";
    case "websites":
      return "/websites-api";
    case "erp":
    default:
      return "/api/v1";
  }
}

export function resolveClientApiUrl(origin: string, path: string): { url: string; path: string } {
  const { target, path: resolvedPath } = resolveApiTarget(path);
  const basePath = resolveClientApiBasePath(target);
  const root = `${origin.replace(/\/$/, "")}${basePath}`;
  const apiPath = resolvedPath.startsWith("/") ? resolvedPath : `/${resolvedPath}`;
  return { url: `${root}${apiPath}`, path: apiPath };
}

